import { store } from '@/index';

import { round, toCM, toKG, toMeter } from '@utils/numberUtils';

/**
 * get all actions from the consignment
 */
export const getAllActions = (consignment) => {
  let actions = [...consignment.actions];

  consignment.goods.forEach((goods) => {
    getGoodsActions(goods).forEach((action) => actions.push(action));
  });

  return actions;
};

export const getGoodsActions = (goods) => {
  let actions = [...goods.actions];
  goods.containedGoods?.actions?.forEach((action) => {
    actions.push(action);
  });
  return actions;
};

export const updateConsignmentInList = (state, newConsignment) => {
  return [...state.consignments]?.map((consignment) => {
    if (consignment.id === newConsignment.id) {
      return { ...newConsignment };
    } else return consignment;
  });
};

export const getConsignmentStatus = (consignment) => {
  let status = 'watchTower__status';

  switch (consignment.status) {
    case 'draft':
    default:
      status += '--draft';
      break;
    case 'confirmed':
      status += '--confirmed';
      break;
    case 'in_transit':
      status += '--in-transit';
      break;
    case 'completed':
      status += '--completed';
      break;
    case 'cancelled':
      status += '--cancelled';
      break;
    case 'partially_planned':
      status += '--partially-planned';
      break;
    case 'planned':
      status += '--planned';
      break;
  }

  return status;
};

/**
 * get all actions from the consignment
 */
export const getAllGoods = (consignment) => {
  let goods = [...consignment.goods];

  goods.forEach((good) => {
    good?.entity?.containedGoods?.forEach((innerGoods) => goods.push(innerGoods));
  });

  return goods?.map((association) => association.entity);
};

export const getTotalQuantityGoods = (goods, depth = 0) => {
  if (!goods || goods?.length < 1) return 0;
  const goodsWeightsAndLoadMetersAreTotalFields =
    store?.getState()?.platform?.platform.planningSettings?.goodsWeightsAndLoadMetersAreTotalFields;

  return round(
    goods.reduce((acc, good) => {
      const hasContainedGoods = good?.entity?.containedGoods?.length > 0;
      const isTransportEquipment = good?.entity?.type === 'transportEquipment';

      if (goodsWeightsAndLoadMetersAreTotalFields) {
        return acc + good?.entity?.quantity;
      }

      return (
        acc +
        good?.entity?.quantity *
          (hasContainedGoods
            ? getTotalQuantityGoods(good?.entity?.containedGoods, depth + 1)
            : isTransportEquipment
            ? 0
            : 1)
      );
    }, 0),
    3
  );
};

export const getTotalCBM = (goods, depth = 0) => {
  if (!goods || goods?.length < 1) return 0;

  return round(
    goods.reduce((totalCBM, good) => {
      // Safely access nested properties, default quantity to 0
      const quantity = good?.entity?.quantity || 0;

      // If quantity is 0, this good adds no volume, skip to next
      if (quantity === 0) {
        return totalCBM;
      }

      // Get dimensions in Meters. Default missing values to 0.
      // Using the correct unit for each dimension.
      const widthM = toMeter(good?.entity?.width?.value, good?.entity?.width?.unit);
      const lengthM = toMeter(good?.entity?.length?.value, good?.entity?.length?.unit);
      const heightM = toMeter(good?.entity?.height?.value, good?.entity?.height?.unit);

      // Calculate volume for a single item in CBM.
      // If any dimension was 0, the single item volume will be 0.
      const singleItemCBM = widthM * lengthM * (heightM > 0 ? heightM : 1.8);

      // Add the total volume for this type of good (quantity * single item volume)
      // to the accumulator.
      return totalCBM + quantity * singleItemCBM;
    }, 0),
    3
  );
};

export const getTotalLDM = (goods, depth = 0) => {
  if (!goods || goods?.length < 1) return 0;

  const goodsWeightsAndLoadMetersAreTotalFields =
    store?.getState()?.platform?.platform.planningSettings?.goodsWeightsAndLoadMetersAreTotalFields;

  return round(
    goods.reduce(
      (acc, good) =>
        acc + (round(good?.entity?.loadMeters?.value || 0, 5) > 0)
          ? (goodsWeightsAndLoadMetersAreTotalFields ? 1 : good?.entity?.quantity || 1) *
            round(good?.entity?.loadMeters?.value || 0, 5)
          : 0,
      0
    ),
    3
  );
};

export const getTotalKG = (goods, depth = 0) => {
  if (!goods || goods?.length < 1) return 0;

  const goodsWeightsAndLoadMetersAreTotalFields =
    store?.getState()?.platform?.platform.planningSettings?.goodsWeightsAndLoadMetersAreTotalFields;

  return round(
    goods.reduce(
      (acc, good) =>
        acc +
        (goodsWeightsAndLoadMetersAreTotalFields ? 1 : good?.entity?.quantity) *
          toKG(good?.entity?.weight?.value || 0, good?.entity?.weight?.unit || 'kg'),

      0
    ),
    3
  );
};

export const getTotalTransportEquipmentQuantity = (goods, depth = 0) => {
  if (!goods || goods?.length < 1) return 0;
  return round(
    goods
      .filter((association) => association.entity.type === 'transportEquipment')
      .reduce(
        (acc, good) =>
          acc +
          good?.entity?.quantity +
          (good?.entity?.containedGoods?.length > 0
            ? getTotalTransportEquipmentQuantity(good?.entity?.containedGoods, depth + 1)
            : 0),
        0
      ),
    3
  );
};

export const hasADR = (goods, depth = 0) => {
  if (!goods || goods?.length < 1) return false;

  return goods.some((good) => good.entity?.adr || hasADR(good.entity?.containedGoods, depth));
};
